import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store/redux/store/store';
import { Link, useLocation } from 'react-router-dom';
import AppIcon from '../AppIcon';
import Button from '../ui/Button';
import { cn } from '../../utils/cn';
import { useNotificationModules } from '../../hooks/useNotificationModules';
import type { NotificationModuleView } from '../../api/General/NotificationModules';
import {
  cadenceLabel,
  channelLabel,
  matchModuleForPath,
  moduleSummary,
  moduleTitle,
  typeLabel,
} from './notificationModuleCopy';

/**
 * CO-375 / owner decision E13 — the INLINE opt-in prompt HR sees on the first visit to a module.
 *
 * <p>Not a modal (CLAUDE.md "modal as first thought" ban): a band at the top of the page content that the
 * page stays usable under. Shown to the HR role only (Admin answers from Notification management, whose
 * Modules tab carries the same "Keep on" / "Turn on new" answers for review and newTypes modules; every
 * other role never calls the endpoint). Three modes come from the server's `promptMode`:
 * <ul>
 *   <li><b>optIn</b> — never asked: "Turn on" = On, "Not now" = Off (the answer is recorded, so the prompt
 *   never returns; the module stays switchable on Notification management).</li>
 *   <li><b>review</b> — backfilled On by the E13 migration: "Keep on" / "Turn off". Sending continues until
 *   HR answers (owner decision 2026-09-28).</li>
 *   <li><b>newTypes</b> — On, but types were added since HR answered: "Turn on" re-answers On; "Not now"
 *   hides it for this browser session only (the new types stay off).</li>
 * </ul>
 * Every sentence is composed from the catalog enums (notificationModuleCopy.ts).</p>
 */
const SESSION_KEY = 'e13.prompt.dismissed';

function readDismissed(): Set<string> {
  try {
    return new Set<string>(JSON.parse(sessionStorage.getItem(SESSION_KEY) ?? '[]'));
  } catch {
    return new Set();
  }
}

function writeDismissed(keys: Set<string>) {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(Array.from(keys)));
  } catch {
    /* private mode: dismissal lasts until reload, which is acceptable */
  }
}

type Done = { key: string; state: 'On' | 'Off' };

const NotificationModulePrompt: React.FC = () => {
  const { t, i18n } = useTranslation();
  const role = useSelector((state: RootState) => state.user.role) as string | undefined;
  const isHr = (role ?? '').toLowerCase() === 'hr';
  const { pathname } = useLocation();
  const { data, setModuleState } = useNotificationModules(isHr);

  const [dismissed, setDismissed] = useState<Set<string>>(() => readDismissed());
  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState<'On' | 'Off' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<Done | null>(null);

  const matched = useMemo<NotificationModuleView | null>(
    () => (data ? matchModuleForPath(data.modules, pathname) : null),
    [data, pathname]
  );

  // A different module (or none) resets the band's local state.
  useEffect(() => {
    setExpanded(false);
    setError(null);
    setDone((d) => (d && d.key === matched?.key ? d : null));
  }, [matched?.key]);

  if (!isHr || !data || !matched) return null;

  const title = moduleTitle(t, matched.key);

  if (done) {
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid="e13-module-prompt-done"
        data-module={matched.key}
        data-state={done.state}
        className="mb-6 flex flex-col gap-3 rounded-xl border border-border bg-muted/40 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
      >
        <p className="flex items-center gap-2 text-sm text-foreground">
          <AppIcon name={done.state === 'On' ? 'BellRing' : 'BellOff'} size={16} className="shrink-0 text-primary" />
          {done.state === 'On'
            ? t('notificationModules.prompt.doneOn', { module: title })
            : t('notificationModules.prompt.doneOff', { module: title })}
          <span className="text-muted-foreground">{t('notificationModules.prompt.changeLater')}</span>
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <Button asChild variant="link" size="sm">
            <Link to="/settings/notification-management?tab=modules">{t('Notification Management')}</Link>
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setDone(null)} aria-label={t('Dismiss')}>
            <AppIcon name="X" size={16} />
          </Button>
        </div>
      </div>
    );
  }

  const mode = matched.promptMode;
  if (!mode || data.masterDisabled || dismissed.has(`${matched.key}:${mode}`)) return null;

  const types = mode === 'newTypes' ? matched.types.filter((x) => x.isNewSinceAnswer) : matched.types;
  if (types.length === 0) return null;

  const copy = {
    optIn: {
      title: t('notificationModules.prompt.optIn.title', { module: title }),
      body: t('notificationModules.prompt.optIn.body', { module: title }),
      yes: t('notificationModules.prompt.turnOn'),
      no: t('notificationModules.prompt.notNow'),
    },
    review: {
      title: t('notificationModules.prompt.review.title', { module: title }),
      body: t('notificationModules.prompt.review.body'),
      yes: t('notificationModules.prompt.keepOn'),
      no: t('notificationModules.prompt.turnOff'),
    },
    newTypes: {
      title: t('notificationModules.prompt.newTypes.title', { module: title }),
      body: t('notificationModules.prompt.newTypes.body'),
      yes: t('notificationModules.prompt.turnOn'),
      no: t('notificationModules.prompt.notNow'),
    },
  }[mode];

  const answer = async (state: 'On' | 'Off') => {
    if (mode === 'newTypes' && state === 'Off') {
      // "Not now" on new types records nothing: the module stays On for what HR already accepted.
      const next = new Set(dismissed).add(`${matched.key}:${mode}`);
      writeDismissed(next);
      setDismissed(next);
      return;
    }
    setSaving(state);
    setError(null);
    const err = await setModuleState(matched.key, state, 'prompt');
    setSaving(null);
    if (err) {
      setError(t('notificationModules.prompt.error'));
      return;
    }
    setDone({ key: matched.key, state });
  };

  const detailsId = `e13-prompt-details-${matched.key}`;

  return (
    <section
      aria-labelledby={`e13-prompt-title-${matched.key}`}
      data-testid="e13-module-prompt"
      data-module={matched.key}
      data-mode={mode}
      className="mb-6 rounded-xl border border-primary/25 bg-primary/[0.04] p-4 sm:p-5"
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
            <AppIcon name={mode === 'review' ? 'BellRing' : 'Bell'} size={18} />
          </span>
          <div className="min-w-0 space-y-1">
            <h2 id={`e13-prompt-title-${matched.key}`} className="text-sm font-semibold text-foreground">
              {copy.title}
            </h2>
            <p className="max-w-[70ch] text-sm text-muted-foreground">{copy.body}</p>
            <p className="max-w-[70ch] text-sm text-foreground" data-testid="e13-module-prompt-summary">
              {moduleSummary(t, i18n.language, types)}
            </p>
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-controls={detailsId}
              data-testid="e13-module-prompt-details"
              className="inline-flex items-center gap-1 rounded text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {expanded ? t('notificationModules.prompt.hideDetails') : t('notificationModules.prompt.showDetails')}
              <AppIcon
                name="ChevronDown"
                size={14}
                className={cn('transition-transform duration-200 motion-reduce:transition-none', expanded && 'rotate-180')}
              />
            </button>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2 md:pt-0.5">
          <Button
            size="sm"
            onClick={() => answer('On')}
            loading={saving === 'On'}
            disabled={saving !== null}
            data-testid="e13-module-prompt-on"
            className="active:translate-y-px"
          >
            {copy.yes}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => answer('Off')}
            loading={saving === 'Off'}
            disabled={saving !== null}
            data-testid="e13-module-prompt-off"
          >
            {copy.no}
          </Button>
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-3 flex items-center gap-2 text-sm text-destructive">
          <AppIcon name="AlertCircle" size={16} className="shrink-0" />
          {error}
        </p>
      )}

      {expanded && (
        <ul
          id={detailsId}
          data-testid="e13-module-prompt-type-list"
          className="mt-4 max-h-80 divide-y divide-border overflow-y-auto rounded-lg border border-border bg-background"
        >
          {types.map((type) => (
            <li
              key={type.id}
              data-testid={`e13-module-prompt-type-${type.id}`}
              className="flex flex-col gap-1 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
            >
              <span className="text-sm text-foreground">{typeLabel(t, type.name)}</span>
              <span className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                <span className="rounded bg-muted px-1.5 py-0.5">{cadenceLabel(t, type.cadence)}</span>
                {type.channels.map((c) => (
                  <span key={c} className="rounded border border-border px-1.5 py-0.5">
                    {channelLabel(t, c)}
                  </span>
                ))}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default NotificationModulePrompt;
